/** @jsx jsx */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { jsx } from '@emotion/core';
import SidebarTemplate from 'components/templates/SidebarTemplate';
import FormContents from 'components/atoms/Form/FormContents';
import FormField from 'components/atoms/Form/FormField';
import TextForm from 'components/molecules/TextForm';
import RadioSelectForm from 'components/molecules/RadioSelectForm';
import Button from 'components/atoms/Button';
import ConfirmActionModal, { ActionType } from 'components/organismos/ConfirmActionModal';
import useToastNotification from 'hooks/useToastNotification';
import { LaborCostCustomItemMst, getList, save } from 'api/laborCostCustomItemMst';

const LaborCostCustomItemCreatePage: React.FC = () => {
  const { customId } = useParams();
  const history = useHistory();
  const { successNotification, errorNotification } = useToastNotification();
  const isEditMode = !!customId;

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<LaborCostCustomItemMst>({
    customId: null,
    companyCode: '',
    customItemName: '',
    dispOrder: 0,
    includeAttend: true,
    includeSales: true,
  });

  useEffect(() => {
    if (isEditMode) {
      const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
      getList(companyCode).then((list) => {
        const found = list.find((item) => item.customId === customId);
        if (found) {
          setEditItem(found);
        }
      });
    }
  }, [customId, isEditMode]);

  const handleClickSave = useCallback(() => {
    if (!editItem.customItemName.trim()) {
      errorNotification('項目名を入力してください。');
      return;
    }
    if (editItem.customItemName.trim().length > 100) {
      errorNotification('項目名は100文字以内で入力してください。');
      return;
    }
    setConfirmModalOpen(true);
  }, [editItem.customItemName, errorNotification]);

  const handleConfirmSave = useCallback(() => {
    setConfirmModalOpen(false);
    const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
    const loginStaffCode = sessionStorage.getItem('loginUser.staffCode') || '';

    save(companyCode, loginStaffCode, editItem)
      .then(() => {
        successNotification(isEditMode ? '更新しました。' : '登録しました。');
        history.goBack();
      })
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.defaultMessage) {
          errorNotification(error.response.data.defaultMessage);
        } else {
          errorNotification('サーバー側でエラーが発生しました。');
        }
      });
  }, [editItem, isEditMode, history, successNotification, errorNotification]);

  return (
    <SidebarTemplate pageTitle={isEditMode ? '人件費カスタム項目編集' : '人件費カスタム項目作成'}>
      <FormContents>
        <p>
          {isEditMode
            ? '人件費カスタム項目を編集します。項目名を入力し、勤怠管理・損益管理それぞれの人件費に含めるかどうかを選択してください。'
            : '人件費カスタム項目を登録します。項目名を入力し、勤怠管理・損益管理それぞれの人件費に含めるかどうかを選択してください。'
          }
        </p>

        <FormField>
          <TextForm
            name="dispOrder"
            label="表示順"
            value={String(editItem.dispOrder)}
            onChange={(e: any) =>
              setEditItem({ ...editItem, dispOrder: Number(e.target.value) || 0 })
            }
          />
        </FormField>

        <FormField>
          <TextForm
            name="customItemName"
            label="項目名"
            required={true}
            value={editItem.customItemName}
            onChange={(e: any) =>
              setEditItem({ ...editItem, customItemName: e.target.value })
            }
          />
        </FormField>

        <FormField>
          <RadioSelectForm
            label="勤怠管理"
            items={[
              { label: '人件費に含める', value: 'true' },
              { label: '人件費に含めない', value: 'false' },
            ]}
            name="includeAttend"
            value={String(editItem.includeAttend)}
            setValue={(e) =>
              setEditItem({ ...editItem, includeAttend: e.target.value === 'true' })
            }
          />
        </FormField>

        <FormField>
          <RadioSelectForm
            label="損益管理"
            items={[
              { label: '人件費に含める', value: 'true' },
              { label: '人件費に含めない', value: 'false' },
            ]}
            name="includeSales"
            value={String(editItem.includeSales)}
            setValue={(e) =>
              setEditItem({ ...editItem, includeSales: e.target.value === 'true' })
            }
          />
        </FormField>

        <div style={{ display: 'flex', marginTop: '16px' }}>
          <div style={{ marginRight: '12px' }}>
            <Button
              text={isEditMode ? '人件費カスタム項目を更新' : '人件費カスタム項目を登録'}
              onClick={handleClickSave}
            />
          </div>
          <Button text="戻る" ghost={true} onClick={() => history.goBack()} />
        </div>
      </FormContents>

      <ConfirmActionModal
        open={confirmModalOpen}
        closeHandler={() => setConfirmModalOpen(false)}
        onSubmit={handleConfirmSave}
        actionType={isEditMode ? ActionType.UPDATE : ActionType.CREATE}
      />
    </SidebarTemplate>
  );
};

export default LaborCostCustomItemCreatePage;
